// Additional country content for SEO destination pages
import type { CountryContent } from "./countryContent";

export const newCountryContent: Record<string, CountryContent> = {
  "Suède": {
    slug: "suede",
    flag: "🇸🇪",
    description: {
      fr: "La Suède séduit par ses paysages nordiques époustouflants : forêts infinies, lacs cristallins, aurores boréales en Laponie et archipels préservés. Stockholm, la capitale sur l'eau, marie histoire viking et design moderne.",
      en: "Sweden captivates with stunning Nordic landscapes: endless forests, crystal-clear lakes, northern lights in Lapland, and pristine archipelagos. Stockholm, the capital on water, blends Viking history with modern design.",
      es: "Suecia seduce con sus paisajes nórdicos impresionantes: bosques infinitos, lagos cristalinos, auroras boreales en Laponia y archipiélagos preservados. Estocolmo, la capital sobre el agua, combina historia vikinga y diseño moderno."
    },
    extendedDescription: {
      fr: "De la cosmopolite Stockholm aux terres sauvages de Laponie, la Suède offre une expérience unique entre nature préservée et innovation. L'hiver révèle les aurores boréales et le célèbre hôtel de glace, tandis que l'été dévoile le soleil de minuit.",
      en: "From cosmopolitan Stockholm to the wild lands of Lapland, Sweden offers a unique experience between preserved nature and innovation. Winter reveals northern lights and the famous ice hotel, while summer unveils the midnight sun.",
      es: "Desde la cosmopolita Estocolmo hasta las tierras salvajes de Laponia, Suecia ofrece una experiencia única entre naturaleza preservada e innovación."
    },
    keywords: {
      fr: ["voyage suède", "stockholm", "laponie suédoise", "aurores boréales suède"],
      en: ["sweden trip", "stockholm", "swedish lapland", "northern lights sweden"],
      es: ["viaje suecia", "estocolmo", "laponia sueca", "auroras boreales suecia"]
    },
    highlights: ["Stockholm", "Göteborg", "Laponie", "Abisko", "Malmö", "Kiruna"],
    bestTimeToVisit: {
      fr: "Juin à Août (été) ou Décembre à Mars (aurores boréales)",
      en: "June to August (summer) or December to March (northern lights)",
      es: "Junio a Agosto (verano) o Diciembre a Marzo (auroras boreales)"
    },
    relatedCountries: ["Norvège", "Finlande", "Danemark", "Islande"]
  },
  "Norvège": {
    slug: "norvege",
    flag: "🇳🇴",
    description: {
      fr: "La Norvège émerveille par ses fjords majestueux, ses montagnes vertigineuses et ses aurores boréales dansantes. Des villages de pêcheurs des Lofoten aux glaciers du Svalbard, c'est un paradis pour les amoureux de nature.",
      en: "Norway amazes with its majestic fjords, soaring mountains, and dancing northern lights. From the fishing villages of Lofoten to Svalbard's glaciers, it's a paradise for nature lovers.",
      es: "Noruega maravilla con sus fiordos majestuosos, montañas vertiginosas y auroras boreales danzantes. De los pueblos pesqueros de Lofoten a los glaciares de Svalbard, es un paraíso para los amantes de la naturaleza."
    },
    extendedDescription: {
      fr: "La Norvège offre des paysages parmi les plus spectaculaires au monde. Le Geirangerfjord et le Nærøyfjord sont classés UNESCO. Les randonnées comme Trolltunga ou Preikestolen révèlent des panoramas à couper le souffle.",
      en: "Norway offers some of the world's most spectacular landscapes. Geirangerfjord and Nærøyfjord are UNESCO listed. Hikes like Trolltunga or Preikestolen reveal breathtaking panoramas.",
      es: "Noruega ofrece algunos de los paisajes más espectaculares del mundo. El Geirangerfjord y el Nærøyfjord son Patrimonio de la UNESCO."
    },
    keywords: {
      fr: ["voyage norvège", "fjords norvège", "lofoten", "aurores boréales norvège"],
      en: ["norway trip", "norwegian fjords", "lofoten islands", "northern lights norway"],
      es: ["viaje noruega", "fiordos noruega", "islas lofoten", "auroras boreales noruega"]
    },
    highlights: ["Oslo", "Bergen", "Îles Lofoten", "Tromsø", "Geirangerfjord", "Cap Nord"],
    bestTimeToVisit: {
      fr: "Mai à Septembre (été) ou Novembre à Mars (aurores boréales)",
      en: "May to September (summer) or November to March (northern lights)",
      es: "Mayo a Septiembre (verano) o Noviembre a Marzo (auroras boreales)"
    },
    relatedCountries: ["Suède", "Finlande", "Islande", "Danemark"]
  },
  "Finlande": {
    slug: "finlande",
    flag: "🇫🇮",
    description: {
      fr: "La Finlande enchante par ses milliers de lacs, ses forêts boréales et la magie de la Laponie. Pays du Père Noël et des aurores boréales, elle offre aussi des saunas authentiques et un design nordique unique.",
      en: "Finland enchants with its thousands of lakes, boreal forests, and Lapland magic. Land of Santa Claus and northern lights, it also offers authentic saunas and unique Nordic design.",
      es: "Finlandia encanta con sus miles de lagos, bosques boreales y la magia de Laponia. País de Papá Noel y auroras boreales, ofrece también saunas auténticas y un diseño nórdico único."
    },
    extendedDescription: {
      fr: "Entre Helsinki la moderne et Rovaniemi le village du Père Noël, la Finlande dévoile une nature préservée. L'été offre le soleil de minuit et les activités lacustres, l'hiver les sports de neige et les igloos sous les aurores.",
      en: "Between modern Helsinki and Rovaniemi, Santa's village, Finland unveils pristine nature. Summer offers midnight sun and lake activities, winter brings snow sports and igloos under the auroras.",
      es: "Entre la moderna Helsinki y Rovaniemi, el pueblo de Papá Noel, Finlandia revela una naturaleza preservada."
    },
    keywords: {
      fr: ["voyage finlande", "laponie finlandaise", "père noël rovaniemi", "aurores boréales finlande"],
      en: ["finland trip", "finnish lapland", "santa claus rovaniemi", "northern lights finland"],
      es: ["viaje finlandia", "laponia finlandesa", "papá noel rovaniemi", "auroras boreales finlandia"]
    },
    highlights: ["Helsinki", "Rovaniemi", "Laponie", "Tampere", "Turku", "Lac Saimaa"],
    bestTimeToVisit: {
      fr: "Décembre à Mars (hiver/aurores) ou Juin à Août (été/soleil de minuit)",
      en: "December to March (winter/auroras) or June to August (summer/midnight sun)",
      es: "Diciembre a Marzo (invierno/auroras) o Junio a Agosto (verano/sol de medianoche)"
    },
    relatedCountries: ["Suède", "Norvège", "Estonie", "Russie"]
  },
  "Belgique": {
    slug: "belgique",
    flag: "🇧🇪",
    description: {
      fr: "La Belgique surprend par sa richesse culturelle : villes médiévales flamandes, art nouveau bruxellois, chocolat d'exception et bières artisanales. Bruges, Gand et Bruxelles offrent un patrimoine architectural remarquable.",
      en: "Belgium surprises with its cultural richness: medieval Flemish cities, Brussels' Art Nouveau, exceptional chocolate, and craft beers. Bruges, Ghent, and Brussels offer remarkable architectural heritage.",
      es: "Bélgica sorprende por su riqueza cultural: ciudades medievales flamencas, art nouveau de Bruselas, chocolate excepcional y cervezas artesanales. Brujas, Gante y Bruselas ofrecen un patrimonio arquitectónico notable."
    },
    extendedDescription: {
      fr: "Petit pays aux multiples facettes, la Belgique marie patrimoine UNESCO et gastronomie renommée. Des canaux de Bruges aux institutions européennes de Bruxelles, en passant par les Ardennes verdoyantes.",
      en: "A small country with multiple facets, Belgium combines UNESCO heritage and renowned gastronomy. From Bruges' canals to Brussels' European institutions, through the green Ardennes.",
      es: "Un pequeño país con múltiples facetas, Bélgica combina patrimonio UNESCO y gastronomía reconocida."
    },
    keywords: {
      fr: ["voyage belgique", "bruges", "bruxelles", "bière belge"],
      en: ["belgium trip", "bruges", "brussels", "belgian beer"],
      es: ["viaje bélgica", "brujas", "bruselas", "cerveza belga"]
    },
    highlights: ["Bruxelles", "Bruges", "Gand", "Anvers", "Liège", "Ardennes"],
    bestTimeToVisit: {
      fr: "Avril à Octobre",
      en: "April to October",
      es: "Abril a Octubre"
    },
    relatedCountries: ["France", "Pays-Bas", "Luxembourg", "Allemagne"]
  },
  "Pays-Bas": {
    slug: "pays-bas",
    flag: "🇳🇱",
    description: {
      fr: "Les Pays-Bas charment par leurs canaux pittoresques, champs de tulipes colorés et moulins emblématiques. Amsterdam vibre entre musées de maîtres et quartiers branchés, tandis que les villages traditionnels préservent l'âme hollandaise.",
      en: "The Netherlands charm with picturesque canals, colorful tulip fields, and iconic windmills. Amsterdam buzzes between master museums and trendy districts, while traditional villages preserve the Dutch soul.",
      es: "Los Países Bajos encantan con sus canales pintorescos, campos de tulipanes coloridos y molinos emblemáticos. Ámsterdam vibra entre museos de maestros y barrios de moda."
    },
    extendedDescription: {
      fr: "Au-delà d'Amsterdam, les Pays-Bas révèlent Rotterdam et son architecture audacieuse, La Haye et ses institutions, et le parc de Keukenhof au printemps. Le vélo est roi dans ce pays plat et accueillant.",
      en: "Beyond Amsterdam, the Netherlands reveal Rotterdam and its bold architecture, The Hague and its institutions, and Keukenhof park in spring. Cycling is king in this flat and welcoming country.",
      es: "Más allá de Ámsterdam, los Países Bajos revelan Rotterdam y su arquitectura audaz, La Haya y sus instituciones, y el parque Keukenhof en primavera."
    },
    keywords: {
      fr: ["voyage pays-bas", "amsterdam", "tulipes hollande", "moulins hollande"],
      en: ["netherlands trip", "amsterdam", "dutch tulips", "holland windmills"],
      es: ["viaje países bajos", "ámsterdam", "tulipanes holanda", "molinos holanda"]
    },
    highlights: ["Amsterdam", "Rotterdam", "La Haye", "Utrecht", "Keukenhof", "Kinderdijk"],
    bestTimeToVisit: {
      fr: "Avril à Mai (tulipes) ou Juin à Septembre",
      en: "April to May (tulips) or June to September",
      es: "Abril a Mayo (tulipanes) o Junio a Septiembre"
    },
    relatedCountries: ["Belgique", "Allemagne", "France", "Royaume-Uni"]
  },
  "Russie": {
    slug: "russie",
    flag: "🇷🇺",
    description: {
      fr: "La Russie fascine par son immensité et sa richesse culturelle : les coupoles colorées de Moscou, les palais impériaux de Saint-Pétersbourg, le Transsibérien légendaire et le lac Baïkal aux eaux cristallines.",
      en: "Russia fascinates with its immensity and cultural richness: Moscow's colorful domes, Saint Petersburg's imperial palaces, the legendary Trans-Siberian, and Lake Baikal's crystal waters.",
      es: "Rusia fascina por su inmensidad y riqueza cultural: las cúpulas coloridas de Moscú, los palacios imperiales de San Petersburgo, el legendario Transiberiano y las aguas cristalinas del lago Baikal."
    },
    extendedDescription: {
      fr: "Du Kremlin aux nuits blanches de Saint-Pétersbourg, la Russie dévoile un patrimoine impérial somptueux. Le Transsibérien traverse des paysages épiques, des steppes aux montagnes de l'Oural.",
      en: "From the Kremlin to Saint Petersburg's white nights, Russia unveils sumptuous imperial heritage. The Trans-Siberian crosses epic landscapes, from steppes to the Ural Mountains.",
      es: "Del Kremlin a las noches blancas de San Petersburgo, Rusia revela un patrimonio imperial suntuoso."
    },
    keywords: {
      fr: ["voyage russie", "moscou", "saint-pétersbourg", "transsibérien"],
      en: ["russia trip", "moscow", "saint petersburg", "trans-siberian"],
      es: ["viaje rusia", "moscú", "san petersburgo", "transiberiano"]
    },
    highlights: ["Moscou", "Saint-Pétersbourg", "Lac Baïkal", "Anneau d'Or", "Kazan", "Vladivostok"],
    bestTimeToVisit: {
      fr: "Mai à Septembre (été) ou Décembre à Février (neige)",
      en: "May to September (summer) or December to February (snow)",
      es: "Mayo a Septiembre (verano) o Diciembre a Febrero (nieve)"
    },
    relatedCountries: ["Mongolie", "Finlande", "Kazakhstan", "Chine"]
  },
  "Azerbaïdjan": {
    slug: "azerbaidjan",
    flag: "🇦🇿",
    description: {
      fr: "L'Azerbaïdjan, carrefour entre Orient et Occident, surprend par sa modernité à Bakou et ses traditions millénaires. Le pays du feu révèle des volcans de boue, des temples zoroastriens et des paysages caucasiens spectaculaires.",
      en: "Azerbaijan, a crossroads between East and West, surprises with Baku's modernity and ancient traditions. The land of fire reveals mud volcanoes, Zoroastrian temples, and spectacular Caucasian landscapes.",
      es: "Azerbaiyán, encrucijada entre Oriente y Occidente, sorprende por su modernidad en Bakú y sus tradiciones milenarias. El país del fuego revela volcanes de barro, templos zoroastrianos y paisajes caucásicos espectaculares."
    },
    extendedDescription: {
      fr: "Bakou marie vieille ville UNESCO et architecture futuriste des Flame Towers. Au-delà, découvrez Sheki et ses caravansérails, Gobustan et ses pétroglyphes, ou les montagnes du Caucase.",
      en: "Baku combines UNESCO old town and futuristic Flame Towers architecture. Beyond, discover Sheki and its caravanserais, Gobustan and its petroglyphs, or the Caucasus Mountains.",
      es: "Bakú combina el casco antiguo UNESCO y la arquitectura futurista de las Flame Towers."
    },
    keywords: {
      fr: ["voyage azerbaïdjan", "bakou", "caucase", "pays du feu"],
      en: ["azerbaijan trip", "baku", "caucasus", "land of fire"],
      es: ["viaje azerbaiyán", "bakú", "cáucaso", "país del fuego"]
    },
    highlights: ["Bakou", "Sheki", "Gobustan", "Quba", "Gabala", "Lahij"],
    bestTimeToVisit: {
      fr: "Avril à Juin et Septembre à Octobre",
      en: "April to June and September to October",
      es: "Abril a Junio y Septiembre a Octubre"
    },
    relatedCountries: ["Géorgie", "Arménie", "Iran", "Turquie"]
  },
  "Congo": {
    slug: "congo",
    flag: "🇨🇩",
    description: {
      fr: "Le Congo fascine par sa nature sauvage exceptionnelle : forêts tropicales abritant gorilles et bonobos, fleuve Congo majestueux et volcans actifs du Virunga. Une destination d'aventure pour les voyageurs intrépides.",
      en: "Congo fascinates with exceptional wild nature: tropical forests sheltering gorillas and bonobos, the majestic Congo River, and Virunga's active volcanoes. An adventure destination for intrepid travelers.",
      es: "El Congo fascina por su naturaleza salvaje excepcional: selvas tropicales que albergan gorilas y bonobos, el majestuoso río Congo y los volcanes activos de Virunga. Un destino de aventura para viajeros intrépidos."
    },
    extendedDescription: {
      fr: "Le parc des Virunga, patrimoine UNESCO, abrite les derniers gorilles de montagne. Le volcan Nyiragongo offre l'un des rares lacs de lave permanents au monde. Une expérience unique pour les amoureux de nature.",
      en: "Virunga Park, a UNESCO heritage site, shelters the last mountain gorillas. Nyiragongo volcano offers one of the world's rare permanent lava lakes. A unique experience for nature lovers.",
      es: "El parque Virunga, patrimonio UNESCO, alberga los últimos gorilas de montaña."
    },
    keywords: {
      fr: ["voyage congo", "gorilles virunga", "nyiragongo", "safari congo"],
      en: ["congo trip", "virunga gorillas", "nyiragongo", "congo safari"],
      es: ["viaje congo", "gorilas virunga", "nyiragongo", "safari congo"]
    },
    highlights: ["Parc des Virunga", "Volcan Nyiragongo", "Kinshasa", "Goma", "Fleuve Congo", "Parc de Kahuzi-Biega"],
    bestTimeToVisit: {
      fr: "Juin à Septembre (saison sèche)",
      en: "June to September (dry season)",
      es: "Junio a Septiembre (temporada seca)"
    },
    relatedCountries: ["Rwanda", "Ouganda", "Tanzanie", "Cameroun"]
  },
  "Togo": {
    slug: "togo",
    flag: "🇹🇬",
    description: {
      fr: "Le Togo, petit pays d'Afrique de l'Ouest, séduit par sa diversité : plages tropicales, collines verdoyantes, villages vaudou traditionnels et marchés colorés. Lomé, la capitale, vibre au rythme de l'Afrique authentique.",
      en: "Togo, a small West African country, seduces with its diversity: tropical beaches, green hills, traditional voodoo villages, and colorful markets. Lomé, the capital, vibrates to the rhythm of authentic Africa.",
      es: "Togo, pequeño país de África Occidental, seduce por su diversidad: playas tropicales, colinas verdes, pueblos vudú tradicionales y mercados coloridos. Lomé, la capital, vibra al ritmo del África auténtica."
    },
    extendedDescription: {
      fr: "Des plages de Lomé aux châteaux Tamberma classés UNESCO, le Togo offre une immersion dans les traditions animistes. Le marché aux fétiches et les cérémonies vaudou révèlent une culture unique.",
      en: "From Lomé beaches to UNESCO-listed Tamberma castles, Togo offers immersion in animist traditions. The fetish market and voodoo ceremonies reveal a unique culture.",
      es: "De las playas de Lomé a los castillos Tamberma UNESCO, Togo ofrece una inmersión en las tradiciones animistas."
    },
    keywords: {
      fr: ["voyage togo", "lomé", "vaudou togo", "tamberma"],
      en: ["togo trip", "lome", "voodoo togo", "tamberma castles"],
      es: ["viaje togo", "lomé", "vudú togo", "tamberma"]
    },
    highlights: ["Lomé", "Kpalimé", "Koutammakou", "Togoville", "Parc de Fazao", "Lac Togo"],
    bestTimeToVisit: {
      fr: "Novembre à Février (saison sèche)",
      en: "November to February (dry season)",
      es: "Noviembre a Febrero (temporada seca)"
    },
    relatedCountries: ["Ghana", "Bénin", "Burkina Faso", "Côte d'Ivoire"]
  },
  "Maroc": {
    slug: "maroc",
    flag: "🇲🇦",
    description: {
      fr: "Le Maroc envoûte par ses médinas labyrinthiques, ses palais ornés et ses paysages contrastés. Des souks de Marrakech aux dunes de Merzouga, des montagnes de l'Atlas aux côtes atlantiques, c'est un voyage sensoriel unique.",
      en: "Morocco enchants with labyrinthine medinas, ornate palaces, and contrasting landscapes. From Marrakech souks to Merzouga dunes, from Atlas Mountains to Atlantic coasts, it's a unique sensory journey.",
      es: "Marruecos hechiza con sus medinas laberínticas, palacios ornamentados y paisajes contrastados. De los zocos de Marrakech a las dunas de Merzouga, del Atlas a las costas atlánticas, es un viaje sensorial único."
    },
    extendedDescription: {
      fr: "Le Maroc offre mille visages : les riads de Fès, les cascades d'Ouzoud, les vallées du Dadès et du Todra, les plages d'Essaouira. Chaque ville impériale raconte une histoire millénaire.",
      en: "Morocco offers a thousand faces: Fez riads, Ouzoud waterfalls, Dades and Todra valleys, Essaouira beaches. Each imperial city tells a millennial story.",
      es: "Marruecos ofrece mil caras: los riads de Fez, las cascadas de Ouzoud, los valles del Dades y Todra, las playas de Essaouira."
    },
    keywords: {
      fr: ["voyage maroc", "marrakech", "désert maroc", "fès médina"],
      en: ["morocco trip", "marrakech", "moroccan desert", "fez medina"],
      es: ["viaje marruecos", "marrakech", "desierto marruecos", "fez medina"]
    },
    highlights: ["Marrakech", "Fès", "Chefchaouen", "Merzouga", "Essaouira", "Ouarzazate"],
    bestTimeToVisit: {
      fr: "Mars à Mai et Septembre à Novembre",
      en: "March to May and September to November",
      es: "Marzo a Mayo y Septiembre a Noviembre"
    },
    relatedCountries: ["Espagne", "Portugal", "Tunisie", "Algérie"]
  },
  "Brésil": {
    slug: "bresil",
    flag: "🇧🇷",
    description: {
      fr: "Le Brésil émerveille par sa démesure : forêt amazonienne, chutes d'Iguaçu spectaculaires, plages de Rio et carnaval endiablé. Un pays-continent où la nature luxuriante côtoie une culture festive et métissée.",
      en: "Brazil amazes with its vastness: Amazon rainforest, spectacular Iguazu Falls, Rio beaches, and wild carnival. A continent-country where lush nature meets festive and mixed culture.",
      es: "Brasil maravilla por su desmesura: selva amazónica, cataratas de Iguazú espectaculares, playas de Río y carnaval desenfrenado. Un país-continente donde la naturaleza exuberante convive con una cultura festiva y mestiza."
    },
    extendedDescription: {
      fr: "Du Pain de Sucre au Pantanal, des favelas de Rio aux coloniaux de Salvador de Bahia, le Brésil dévoile une diversité incroyable. La samba, la capoeira et la gastronomie régionale enrichissent l'expérience.",
      en: "From Sugarloaf to the Pantanal, from Rio's favelas to colonial Salvador de Bahia, Brazil reveals incredible diversity. Samba, capoeira, and regional gastronomy enrich the experience.",
      es: "Del Pan de Azúcar al Pantanal, de las favelas de Río al colonial Salvador de Bahía, Brasil revela una diversidad increíble."
    },
    keywords: {
      fr: ["voyage brésil", "rio de janeiro", "amazonie", "iguaçu"],
      en: ["brazil trip", "rio de janeiro", "amazon", "iguazu falls"],
      es: ["viaje brasil", "río de janeiro", "amazonia", "cataratas iguazú"]
    },
    highlights: ["Rio de Janeiro", "São Paulo", "Salvador", "Iguaçu", "Amazonie", "Fernando de Noronha"],
    bestTimeToVisit: {
      fr: "Avril à Octobre (saison sèche)",
      en: "April to October (dry season)",
      es: "Abril a Octubre (temporada seca)"
    },
    relatedCountries: ["Argentine", "Pérou", "Colombie", "Uruguay"]
  },
  "Chili": {
    slug: "chili",
    flag: "🇨🇱",
    description: {
      fr: "Le Chili étire ses paysages extrêmes sur 4300 km : désert d'Atacama le plus aride au monde, lacs et volcans de Patagonie, glaciers de Torres del Paine et mystérieuse Île de Pâques aux moaïs énigmatiques.",
      en: "Chile stretches its extreme landscapes over 4300 km: the world's driest Atacama Desert, Patagonian lakes and volcanoes, Torres del Paine glaciers, and mysterious Easter Island with its enigmatic moai.",
      es: "Chile extiende sus paisajes extremos a lo largo de 4300 km: el desierto de Atacama más árido del mundo, lagos y volcanes de Patagonia, glaciares de Torres del Paine y la misteriosa Isla de Pascua con sus enigmáticos moáis."
    },
    extendedDescription: {
      fr: "De Valparaíso la colorée aux geysers du Tatio, le Chili offre des contrastes saisissants. La Route Australe traverse des paysages vierges, tandis que la région viticole produit d'excellents vins.",
      en: "From colorful Valparaíso to Tatio geysers, Chile offers striking contrasts. The Carretera Austral crosses virgin landscapes, while the wine region produces excellent wines.",
      es: "Desde la colorida Valparaíso hasta los géiseres del Tatio, Chile ofrece contrastes impresionantes."
    },
    keywords: {
      fr: ["voyage chili", "patagonie chilienne", "atacama", "île de pâques"],
      en: ["chile trip", "chilean patagonia", "atacama desert", "easter island"],
      es: ["viaje chile", "patagonia chilena", "atacama", "isla de pascua"]
    },
    highlights: ["Santiago", "Torres del Paine", "Atacama", "Valparaíso", "Île de Pâques", "Chiloé"],
    bestTimeToVisit: {
      fr: "Novembre à Mars (Patagonie) ou toute l'année (Atacama)",
      en: "November to March (Patagonia) or year-round (Atacama)",
      es: "Noviembre a Marzo (Patagonia) o todo el año (Atacama)"
    },
    relatedCountries: ["Argentine", "Pérou", "Bolivie", "Brésil"]
  },
  "Argentine": {
    slug: "argentine",
    flag: "🇦🇷",
    description: {
      fr: "L'Argentine séduit par ses paysages grandioses : glaciers de Patagonie, pampa infinie, vignobles de Mendoza et chutes d'Iguazú. Buenos Aires vibre au rythme du tango dans ses quartiers mythiques.",
      en: "Argentina seduces with grandiose landscapes: Patagonian glaciers, endless pampas, Mendoza vineyards, and Iguazu Falls. Buenos Aires vibrates to the rhythm of tango in its legendary neighborhoods.",
      es: "Argentina seduce por sus paisajes grandiosos: glaciares de Patagonia, pampa infinita, viñedos de Mendoza y cataratas del Iguazú. Buenos Aires vibra al ritmo del tango en sus barrios míticos."
    },
    extendedDescription: {
      fr: "Du glacier Perito Moreno à la Terre de Feu, l'Argentine dévoile des merveilles naturelles. Le nord-ouest révèle des paysages lunaires, tandis que les estancias offrent une immersion dans la culture gaucho.",
      en: "From Perito Moreno glacier to Tierra del Fuego, Argentina unveils natural wonders. The northwest reveals lunar landscapes, while estancias offer immersion in gaucho culture.",
      es: "Del glaciar Perito Moreno a Tierra del Fuego, Argentina revela maravillas naturales."
    },
    keywords: {
      fr: ["voyage argentine", "patagonie argentine", "buenos aires", "perito moreno"],
      en: ["argentina trip", "argentine patagonia", "buenos aires", "perito moreno glacier"],
      es: ["viaje argentina", "patagonia argentina", "buenos aires", "glaciar perito moreno"]
    },
    highlights: ["Buenos Aires", "Patagonie", "Mendoza", "Iguazú", "Ushuaïa", "Salta"],
    bestTimeToVisit: {
      fr: "Novembre à Mars (Patagonie) ou Mars à Mai (nord)",
      en: "November to March (Patagonia) or March to May (north)",
      es: "Noviembre a Marzo (Patagonia) o Marzo a Mayo (norte)"
    },
    relatedCountries: ["Chili", "Brésil", "Uruguay", "Paraguay"]
  },
  "Bulgarie": {
    slug: "bulgarie",
    flag: "🇧🇬",
    description: {
      fr: "La Bulgarie surprend par son patrimoine méconnu : monastères orthodoxes nichés dans les montagnes, villages traditionnels, plages de la mer Noire et vallée des roses. Une destination authentique hors des sentiers battus.",
      en: "Bulgaria surprises with its lesser-known heritage: Orthodox monasteries nestled in mountains, traditional villages, Black Sea beaches, and the Valley of Roses. An authentic off-the-beaten-path destination.",
      es: "Bulgaria sorprende por su patrimonio desconocido: monasterios ortodoxos enclavados en las montañas, pueblos tradicionales, playas del Mar Negro y el Valle de las Rosas. Un destino auténtico fuera de los caminos trillados."
    },
    extendedDescription: {
      fr: "Du monastère de Rila classé UNESCO à la vieille ville de Plovdiv, la Bulgarie révèle une histoire riche. Les montagnes offrent randonnées et ski, la côte des stations balnéaires accessibles.",
      en: "From UNESCO-listed Rila Monastery to Plovdiv's old town, Bulgaria reveals rich history. Mountains offer hiking and skiing, the coast provides accessible beach resorts.",
      es: "Del monasterio de Rila UNESCO al casco antiguo de Plovdiv, Bulgaria revela una historia rica."
    },
    keywords: {
      fr: ["voyage bulgarie", "sofia", "plovdiv", "monastère rila"],
      en: ["bulgaria trip", "sofia", "plovdiv", "rila monastery"],
      es: ["viaje bulgaria", "sofía", "plovdiv", "monasterio rila"]
    },
    highlights: ["Sofia", "Plovdiv", "Monastère de Rila", "Veliko Tarnovo", "Varna", "Bansko"],
    bestTimeToVisit: {
      fr: "Mai à Septembre (été) ou Décembre à Mars (ski)",
      en: "May to September (summer) or December to March (skiing)",
      es: "Mayo a Septiembre (verano) o Diciembre a Marzo (esquí)"
    },
    relatedCountries: ["Roumanie", "Grèce", "Turquie", "Serbie"]
  },
  "Roumanie": {
    slug: "roumanie",
    flag: "🇷🇴",
    description: {
      fr: "La Roumanie fascine par ses légendes et paysages préservés : châteaux de Transylvanie dont le mythique Bran, villages médiévaux fortifiés, delta du Danube sauvage et Carpates majestueuses.",
      en: "Romania fascinates with its legends and preserved landscapes: Transylvanian castles including mythical Bran, fortified medieval villages, wild Danube Delta, and majestic Carpathians.",
      es: "Rumanía fascina por sus leyendas y paisajes preservados: castillos de Transilvania incluido el mítico Bran, pueblos medievales fortificados, delta del Danubio salvaje y Cárpatos majestuosos."
    },
    extendedDescription: {
      fr: "Au-delà du mythe de Dracula, la Roumanie dévoile des trésors : monastères peints de Bucovine, citadelles saxonnes, Maramureș et ses traditions vivantes. Une Europe de l'Est authentique et préservée.",
      en: "Beyond the Dracula myth, Romania unveils treasures: painted monasteries of Bucovina, Saxon citadels, Maramureș and its living traditions. An authentic, preserved Eastern Europe.",
      es: "Más allá del mito de Drácula, Rumanía revela tesoros: monasterios pintados de Bucovina, ciudadelas sajonas, Maramureș y sus tradiciones vivas."
    },
    keywords: {
      fr: ["voyage roumanie", "transylvanie", "château bran", "bucarest"],
      en: ["romania trip", "transylvania", "bran castle", "bucharest"],
      es: ["viaje rumanía", "transilvania", "castillo bran", "bucarest"]
    },
    highlights: ["Bucarest", "Braşov", "Sibiu", "Sighişoara", "Château de Bran", "Delta du Danube"],
    bestTimeToVisit: {
      fr: "Mai à Septembre",
      en: "May to September",
      es: "Mayo a Septiembre"
    },
    relatedCountries: ["Bulgarie", "Hongrie", "Moldavie", "Ukraine"]
  },
  "Turquie": {
    slug: "turquie",
    flag: "🇹🇷",
    description: {
      fr: "La Turquie enchante par son histoire millénaire et ses paysages variés : Istanbul entre deux continents, Cappadoce féerique aux montgolfières, côte turquoise méditerranéenne et sites antiques exceptionnels.",
      en: "Turkey enchants with millennial history and varied landscapes: Istanbul between two continents, magical Cappadocia with hot air balloons, Mediterranean turquoise coast, and exceptional ancient sites.",
      es: "Turquía encanta por su historia milenaria y paisajes variados: Estambul entre dos continentes, Capadocia mágica con globos aerostáticos, costa turquesa mediterránea y sitios antiguos excepcionales."
    },
    extendedDescription: {
      fr: "Du Grand Bazar aux paysages lunaires de Cappadoce, la Turquie offre une richesse incomparable. Éphèse, Pamukkale et Antalya témoignent de civilisations glorieuses. La cuisine turque est un voyage en soi.",
      en: "From the Grand Bazaar to Cappadocia's lunar landscapes, Turkey offers incomparable richness. Ephesus, Pamukkale, and Antalya testify to glorious civilizations. Turkish cuisine is a journey in itself.",
      es: "Del Gran Bazar a los paisajes lunares de Capadocia, Turquía ofrece una riqueza incomparable."
    },
    keywords: {
      fr: ["voyage turquie", "istanbul", "cappadoce", "côte turquoise"],
      en: ["turkey trip", "istanbul", "cappadocia", "turquoise coast"],
      es: ["viaje turquía", "estambul", "capadocia", "costa turquesa"]
    },
    highlights: ["Istanbul", "Cappadoce", "Éphèse", "Pamukkale", "Antalya", "Bodrum"],
    bestTimeToVisit: {
      fr: "Avril à Juin et Septembre à Novembre",
      en: "April to June and September to November",
      es: "Abril a Junio y Septiembre a Noviembre"
    },
    relatedCountries: ["Grèce", "Géorgie", "Iran", "Bulgarie"]
  },
  "Algérie": {
    slug: "algerie",
    flag: "🇩🇿",
    description: {
      fr: "L'Algérie dévoile des trésors méconnus : Casbah d'Alger, ruines romaines de Timgad et Djémila, oasis du Sahara et massif du Hoggar. Le plus grand pays d'Afrique offre une diversité exceptionnelle.",
      en: "Algeria unveils little-known treasures: Algiers Casbah, Roman ruins of Timgad and Djemila, Saharan oases, and the Hoggar massif. Africa's largest country offers exceptional diversity.",
      es: "Argelia revela tesoros desconocidos: la Casbah de Argel, ruinas romanas de Timgad y Djemila, oasis del Sáhara y el macizo del Hoggar. El país más grande de África ofrece una diversidad excepcional."
    },
    extendedDescription: {
      fr: "Des cités romaines mieux préservées qu'en Italie aux paysages désertiques époustouflants du Tassili n'Ajjer, l'Algérie réserve des découvertes extraordinaires pour les voyageurs aventureux.",
      en: "From Roman cities better preserved than in Italy to breathtaking desert landscapes of Tassili n'Ajjer, Algeria holds extraordinary discoveries for adventurous travelers.",
      es: "De las ciudades romanas mejor conservadas que en Italia a los impresionantes paisajes desérticos del Tassili n'Ajjer, Argelia reserva descubrimientos extraordinarios."
    },
    keywords: {
      fr: ["voyage algérie", "alger casbah", "sahara algérie", "tassili"],
      en: ["algeria trip", "algiers casbah", "algerian sahara", "tassili"],
      es: ["viaje argelia", "casbah argel", "sáhara argelino", "tassili"]
    },
    highlights: ["Alger", "Timgad", "Djémila", "Ghardaïa", "Tassili n'Ajjer", "Hoggar"],
    bestTimeToVisit: {
      fr: "Octobre à Avril (désert) ou Mars à Mai (côte)",
      en: "October to April (desert) or March to May (coast)",
      es: "Octubre a Abril (desierto) o Marzo a Mayo (costa)"
    },
    relatedCountries: ["Tunisie", "Maroc", "Libye", "Niger"]
  },
  "Tunisie": {
    slug: "tunisie",
    flag: "🇹🇳",
    description: {
      fr: "La Tunisie séduit par son patrimoine unique : médinas millénaires, vestiges de Carthage, désert du Sahara et stations balnéaires méditerranéennes. Un concentré d'histoire et de diversité à quelques heures de l'Europe.",
      en: "Tunisia seduces with unique heritage: millennial medinas, Carthage vestiges, Sahara Desert, and Mediterranean beach resorts. A concentrate of history and diversity just hours from Europe.",
      es: "Túnez seduce por su patrimonio único: medinas milenarias, vestigios de Cartago, desierto del Sáhara y estaciones balnearias mediterráneas. Un concentrado de historia y diversidad a pocas horas de Europa."
    },
    extendedDescription: {
      fr: "De Sidi Bou Saïd la bleue aux ksour de Tataouine (décors de Star Wars), la Tunisie offre des contrastes saisissants. Dougga et El Jem témoignent de la grandeur romaine.",
      en: "From blue Sidi Bou Said to Tataouine's ksour (Star Wars sets), Tunisia offers striking contrasts. Dougga and El Jem testify to Roman grandeur.",
      es: "Desde el azul Sidi Bou Said hasta los ksour de Tataouine (escenarios de Star Wars), Túnez ofrece contrastes impresionantes."
    },
    keywords: {
      fr: ["voyage tunisie", "tunis", "carthage", "djerba"],
      en: ["tunisia trip", "tunis", "carthage", "djerba"],
      es: ["viaje túnez", "túnez", "cartago", "djerba"]
    },
    highlights: ["Tunis", "Carthage", "Sidi Bou Saïd", "Djerba", "El Jem", "Tozeur"],
    bestTimeToVisit: {
      fr: "Mars à Mai et Septembre à Novembre",
      en: "March to May and September to November",
      es: "Marzo a Mayo y Septiembre a Noviembre"
    },
    relatedCountries: ["Algérie", "Maroc", "Libye", "Italie"]
  },
  "Macédoine du Nord": {
    slug: "macedoine",
    flag: "🇲🇰",
    description: {
      fr: "La Macédoine du Nord charme par son authenticité : lac d'Ohrid aux eaux cristallines, monastères byzantins, bazars ottomans et montagnes préservées. Une destination balkanique méconnue et accueillante.",
      en: "North Macedonia charms with authenticity: crystal-clear Lake Ohrid, Byzantine monasteries, Ottoman bazaars, and preserved mountains. A lesser-known and welcoming Balkan destination.",
      es: "Macedonia del Norte encanta por su autenticidad: lago Ohrid de aguas cristalinas, monasterios bizantinos, bazares otomanos y montañas preservadas. Un destino balcánico desconocido y acogedor."
    },
    extendedDescription: {
      fr: "Ohrid, la perle des Balkans, est classée UNESCO pour son patrimoine naturel et culturel. Skopje surprend par son architecture éclectique, tandis que les villages de montagne préservent les traditions.",
      en: "Ohrid, the pearl of the Balkans, is UNESCO-listed for natural and cultural heritage. Skopje surprises with eclectic architecture, while mountain villages preserve traditions.",
      es: "Ohrid, la perla de los Balcanes, es patrimonio UNESCO por su herencia natural y cultural."
    },
    keywords: {
      fr: ["voyage macédoine", "lac ohrid", "skopje", "balkans"],
      en: ["north macedonia trip", "lake ohrid", "skopje", "balkans"],
      es: ["viaje macedonia", "lago ohrid", "skopje", "balcanes"]
    },
    highlights: ["Ohrid", "Skopje", "Parc national Mavrovo", "Bitola", "Canyon Matka", "Monastère Saint-Naum"],
    bestTimeToVisit: {
      fr: "Mai à Septembre",
      en: "May to September",
      es: "Mayo a Septiembre"
    },
    relatedCountries: ["Albanie", "Grèce", "Kosovo", "Serbie"]
  },
  "Bosnie-Herzégovine": {
    slug: "bosnie",
    flag: "🇧🇦",
    description: {
      fr: "La Bosnie-Herzégovine séduit par son mélange culturel unique : Sarajevo la multiculturelle, Mostar et son pont emblématique, cascades de Kravice et nature préservée. Un joyau balkanique authentique.",
      en: "Bosnia and Herzegovina seduces with its unique cultural mix: multicultural Sarajevo, Mostar and its iconic bridge, Kravice waterfalls, and preserved nature. An authentic Balkan gem.",
      es: "Bosnia y Herzegovina seduce por su mezcla cultural única: Sarajevo la multicultural, Mostar y su puente emblemático, cascadas de Kravice y naturaleza preservada. Una joya balcánica auténtica."
    },
    extendedDescription: {
      fr: "Où l'Orient rencontre l'Occident, la Bosnie dévoile mosquées, églises et synagogues côte à côte. Les montagnes offrent randonnées et rafting, la cuisine marie influences ottomanes et méditerranéennes.",
      en: "Where East meets West, Bosnia reveals mosques, churches, and synagogues side by side. Mountains offer hiking and rafting, cuisine blends Ottoman and Mediterranean influences.",
      es: "Donde Oriente se encuentra con Occidente, Bosnia revela mezquitas, iglesias y sinagogas una al lado de la otra."
    },
    keywords: {
      fr: ["voyage bosnie", "sarajevo", "mostar", "balkans"],
      en: ["bosnia trip", "sarajevo", "mostar", "balkans"],
      es: ["viaje bosnia", "sarajevo", "mostar", "balcanes"]
    },
    highlights: ["Sarajevo", "Mostar", "Cascades de Kravice", "Blagaj", "Jajce", "Travnik"],
    bestTimeToVisit: {
      fr: "Mai à Septembre",
      en: "May to September",
      es: "Mayo a Septiembre"
    },
    relatedCountries: ["Croatie", "Serbie", "Monténégro", "Slovénie"]
  },
  "Panama": {
    slug: "panama",
    flag: "🇵🇦",
    description: {
      fr: "Le Panama impressionne par son célèbre canal reliant deux océans, ses gratte-ciel modernes et sa nature luxuriante. Des îles San Blas aux forêts tropicales, c'est un carrefour entre Amérique centrale et du Sud.",
      en: "Panama impresses with its famous canal linking two oceans, modern skyscrapers, and lush nature. From San Blas islands to tropical forests, it's a crossroads between Central and South America.",
      es: "Panamá impresiona por su famoso canal que une dos océanos, sus rascacielos modernos y su naturaleza exuberante. De las islas San Blas a los bosques tropicales, es un cruce entre América Central y del Sur."
    },
    extendedDescription: {
      fr: "Le canal de Panama reste une merveille d'ingénierie à visiter. Les archipels de San Blas et Bocas del Toro offrent des plages paradisiaques, tandis que le Darién préserve une jungle impénétrable.",
      en: "The Panama Canal remains an engineering marvel to visit. San Blas and Bocas del Toro archipelagos offer paradise beaches, while Darién preserves impenetrable jungle.",
      es: "El Canal de Panamá sigue siendo una maravilla de ingeniería. Los archipiélagos de San Blas y Bocas del Toro ofrecen playas paradisíacas."
    },
    keywords: {
      fr: ["voyage panama", "canal panama", "san blas", "bocas del toro"],
      en: ["panama trip", "panama canal", "san blas islands", "bocas del toro"],
      es: ["viaje panamá", "canal panamá", "islas san blas", "bocas del toro"]
    },
    highlights: ["Panama City", "Canal de Panama", "San Blas", "Bocas del Toro", "Boquete", "Casco Viejo"],
    bestTimeToVisit: {
      fr: "Décembre à Avril (saison sèche)",
      en: "December to April (dry season)",
      es: "Diciembre a Abril (temporada seca)"
    },
    relatedCountries: ["Costa Rica", "Colombie", "Nicaragua", "Guatemala"]
  }
};
